// hooks
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// styles
import { StyledTypographyFontMedium } from '../useGetColumns.styles';

// types
import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import type { Period } from 'components/shared/index';
import type { TFunction } from 'i18next';

// utils
import { dataChipValue } from '../useGetColumns.utils';
import { getCommitmentHours } from '../../utils/getCommitmentHours';

// constants
import { BALANCE_COLUMN_FIELDS } from '../../RosterPlan.consts';

/**
 * Derived from the list both grids pin on, so a counter cannot exist here under a name that is
 * never pinned. `commitmentHours` is the one pinned field that is not a balance.
 */
type CounterField = Exclude<
  (typeof BALANCE_COLUMN_FIELDS)[number],
  'commitmentHours'
>;

type CounterColumnDescriptor = {
  /** The row field to read. `balanceWeek` shows the same `balance` value as the month counter. */
  valueField: string;
  labelKey: string;
  /** The `rosterPlanColumnsVisibility` flag that switches this counter on. */
  preferenceKey: string;
  /** The four commitment counters show a shortfall as a negative number, with a tooltip. */
  flipValue?: boolean;
  /** Counters that only make sense in some periods. Absent means: shown in every period. */
  isShownInPeriod?: (period: Period | null) => boolean;
};

/**
 * Everything that differs between the eight balance counters. Keyed by field, so leaving one out
 * is a compile error rather than a column that silently disappears.
 */
const COUNTER_COLUMNS: Record<CounterField, CounterColumnDescriptor> = {
  balanceWeek: {
    valueField: 'balance',
    labelKey: 'dataGrid.commitmentBalanceWeek',
    preferenceKey: 'showBalanceWeek',
    flipValue: true,
    /**
     * `getRosterPlanColumnsVisibility` already ANDs `showBalanceWeek` with `period === 'week'`, so
     * in the running screen this repeats a decision taken upstream. Kept so the hook's own output
     * does not depend on which caller built the visibility object.
     */
    isShownInPeriod: (period) => period === 'week'
  },
  balance: {
    valueField: 'balance',
    labelKey: 'dataGrid.commitmentBalance',
    preferenceKey: 'showBalanceMonth',
    flipValue: true,
    isShownInPeriod: (period) => period !== 'fourWeeks'
  },
  cumulative: {
    valueField: 'cumulative',
    labelKey: 'dataGrid.cumulativeBalance',
    preferenceKey: 'showCumulativeBalance',
    flipValue: true
  },
  worked: {
    valueField: 'worked',
    labelKey: 'dataGrid.workedBalance',
    preferenceKey: 'showWorkedBalance',
    flipValue: true
  },
  leave: {
    valueField: 'leave',
    labelKey: 'dataGrid.leaveBalance',
    preferenceKey: 'showLeaveBalance'
  },
  year: {
    valueField: 'year',
    labelKey: 'dataGrid.yearlyLeaveBalance',
    preferenceKey: 'showYearlyLeaveBalance'
  },
  plb: {
    valueField: 'plb',
    labelKey: 'dataGrid.plbBalance',
    preferenceKey: 'showPlbBalance'
  },
  plbYear: {
    valueField: 'plbYear',
    labelKey: 'dataGrid.plbYearBalance',
    preferenceKey: 'showPlbYearBalance'
  }
};

/**
 * The order the counters are rendered and pinned in, taken from `BALANCE_COLUMN_FIELDS` itself
 * rather than repeated here — reordering the pinned list reorders the columns with it.
 */
const COUNTER_FIELDS = BALANCE_COLUMN_FIELDS.filter(
  (field): field is CounterField => field !== 'commitmentHours'
);

const commonFieldProps: Pick<GridColDef, 'width' | 'headerAlign' | 'align'> = {
  width: 70,
  headerAlign: 'center',
  align: 'center'
};

const makeCounterColumn = (
  field: CounterField,
  t: TFunction
): GridColDef<GridValidRowModel> => {
  const { valueField, labelKey, flipValue = false } = COUNTER_COLUMNS[field];

  return {
    field,
    headerName: t(labelKey),
    ...commonFieldProps,
    renderCell: ({ row }) =>
      dataChipValue({
        value: row[valueField],
        flipValue,
        tooltipValue: flipValue ? t('dataGrid.workedInsufficient') : ''
      })
  };
};

/**
 * The open requests grid has no counters of its own, but it has to keep the same right hand columns
 * as the employees grid for the two to line up, so it renders an empty column per counter shown there.
 */
const makeEmptyColumn = (field: string): GridColDef<GridValidRowModel> => ({
  field,
  headerName: '',
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  resizable: false,
  align: 'center',
  headerAlign: 'center',
  width: 70,
  renderHeader: () => '',
  renderCell: () => null
});

/**
 * The counter columns pinned to the right of both grids: the commitment hours and the eight
 * balances, each shown only when its user preference is on.
 *
 * Two counters are period bound as well. The placeholders the open requests grid renders follow the
 * same preference and period conditions, so both grids pin the same right hand columns and their
 * day columns line up.
 */
const useCounterColumns = ({
  period = 'month',
  isOpenRequests = false,
  rosterPlanColumnsVisibility
}: {
  period: Period | null;
  isOpenRequests?: boolean;
  rosterPlanColumnsVisibility?: { [key: string]: boolean };
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'rosterPlan'
  });

  return useMemo<GridColDef<GridValidRowModel>[]>(() => {
    const enabledCounters = COUNTER_FIELDS.filter(
      (field) =>
        !!rosterPlanColumnsVisibility?.[COUNTER_COLUMNS[field].preferenceKey] &&
        (COUNTER_COLUMNS[field].isShownInPeriod?.(period) ?? true)
    );

    return [
      ...(rosterPlanColumnsVisibility?.showCommitmentHours
        ? [
            {
              field: 'commitmentHours',
              headerName: t('dataGrid.commitmentHours'),
              ...commonFieldProps,
              renderCell: ({ row }) => {
                const totalHours = getCommitmentHours({
                  days: row.days,
                  isOpenRequests
                });

                return (
                  <StyledTypographyFontMedium component="span">
                    {totalHours ? totalHours.toFixed(1) : 0}
                  </StyledTypographyFontMedium>
                );
              }
            } as GridColDef<GridValidRowModel>
          ]
        : []),
      ...(isOpenRequests
        ? enabledCounters.map((field) => makeEmptyColumn(field))
        : enabledCounters.map((field) => makeCounterColumn(field, t)))
    ];
  }, [t, period, isOpenRequests, rosterPlanColumnsVisibility]);
};

export default useCounterColumns;
